import {
  ChangeDetectionStrategy,
  AfterViewInit,
  Component,
  OnDestroy,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AssignGroupUserCardComponent } from './assign-group-user-card/assign-group-user-card.component';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  fromEvent,
  Subscription,
} from 'rxjs';
import { TranslocoPipe } from '@jsverse/transloco';
import { LoaderComponent } from '../../../../ui-components/lib/loader/loader.component';
import { ModalServiceService } from '../../../../ui-components/lib/modal/modal-service.service';
import { SearchIconComponent } from '@kuiper/icons';
import { SearchMinLength } from '../../enums/search-min-length';
import { Option } from '../../models/option.model';

@Component({
  selector: 'lib-assign-group-users',
  imports: [
    FormsModule,
    AssignGroupUserCardComponent,
    LoaderComponent,
    TranslocoPipe,
    SearchIconComponent,
  ],
  templateUrl: './assign-group-users.component.html',
  styleUrl: './assign-group-users.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class AssignGroupUsersComponent implements AfterViewInit, OnDestroy {
  modalService = inject(ModalServiceService);
  subscriptions = new Subscription();
  searchInput = viewChild<ElementRef>('search');

  groupUsersCards = input<Option[]>([]);
  selectedGroupUsersCards = input<Option[]>([]);
  isLoading = input<boolean>();
  currentPage = input<number>(1);
  totalPages = input<number>(1);
  itemType = input<'User' | 'Group'>('User');
  assignItemsModalTitle = input<string>('');
  isSubmitLoading = input<boolean>(false);
  selectGroupUser = output<Option>();
  unselectGroupUser = output<Option>();
  closeModal = output();
  saveAssignedGroupUsers = output();
  filterGroupUsersCards = output<string>();
  paginateGroupUsersCards = output<number>();
  isSearching = signal<boolean>(false);
  isMoreThanThreeCharacters = signal<boolean>(false);
  minSearchLength = signal<number>(SearchMinLength.length);
  cancelModal = output();

  ngAfterViewInit(): void {
    if (this.searchInput()) {
      this.subscriptions.add(
        fromEvent((<ElementRef>this.searchInput()).nativeElement, 'input')
          .pipe(filter(Boolean), debounceTime(600), distinctUntilChanged())
          .subscribe(() => {
            this.onSearchGroupUser(
              (<ElementRef>this.searchInput()).nativeElement.value
            );
          })
      );
    }
  }

  onSelectGroupUser(user: Option) {
    this.selectGroupUser.emit(user);
  }

  removeGroupUser(user: Option) {
    this.unselectGroupUser.emit(user);
  }

  onCancel() {
    this.cancelModal.emit();
    this.modalService.modalContent.set({
      isOpen: false,
    });
  }

  onSubmitAssignedGroupUsers() {
    this.saveAssignedGroupUsers.emit();
    this.onCancel();
  }

  onSearchGroupUser(search: string) {
    if (search.length >= this.minSearchLength()) {
      this.isMoreThanThreeCharacters.set(true);
      this.filterGroupUsersCards.emit(search);
      this.isSearching.set(!!search.length);
    } else if (
      this.isMoreThanThreeCharacters() &&
      search.length < this.minSearchLength()
    ) {
      this.isMoreThanThreeCharacters.set(false);
      this.filterGroupUsersCards.emit('');
    }
  }

  onScroll(event: Event) {
    const { scrollTop, scrollHeight, clientHeight } =
      event.target as unknown as {
        scrollTop: number;
        scrollHeight: number;
        clientHeight: number;
      };
    if (
      scrollTop + clientHeight >= scrollHeight - 200 &&
      !this.isLoading() &&
      this.currentPage() < this.totalPages()
    ) {
      this.paginateGroupUsersCards.emit(this.currentPage() + 1);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
